import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Surface, Button, Input } from "@/components/ui-kit";
import VehicleSearchSelect from "@/components/VehicleSearchSelect";
import { formatDate } from "@/lib/format";
import { downloadCsv, printElement } from "@/lib/csv";
import { Gauge, CheckCircle, CaretUp, CaretDown, MagnifyingGlass, DownloadSimple, Printer } from "@phosphor-icons/react";

export default function OdometerUpdate() {
    const [vehicles, setVehicles] = useState([]);
    const [vehicleId, setVehicleId] = useState("");
    const [newOdo, setNewOdo] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Stale-km tracker state
    const [sortDir, setSortDir] = useState("desc"); // "desc" = oldest updates first
    const [minDays, setMinDays] = useState(""); // custom filter, e.g. "show vehicles not updated for ≥ N days"
    const [staleSearch, setStaleSearch] = useState("");
    const staleTableRef = useRef(null);

    const load = () => api.get("/vehicles").then((r) => setVehicles(r.data));
    useEffect(() => { load(); }, []);

    const selected = useMemo(
        () => vehicles.find((v) => v.id === vehicleId) || null,
        [vehicles, vehicleId]
    );

    const prevOdo = selected?.odometer || 0;
    const lastUpdated = selected?.odometer_updated_at || selected?.updated_at || selected?.created_at || null;

    // Per-vehicle "days since last odometer update". Falls back to created_at when never updated.
    const staleRows = useMemo(() => {
        const now = Date.now();
        return vehicles.map((v) => {
            const lu = v.odometer_updated_at || v.updated_at || v.created_at || null;
            const days = lu ? Math.max(0, Math.floor((now - new Date(lu).getTime()) / 86400000)) : null;
            const everUpdated = !!v.odometer_updated_at;
            return { ...v, _lastUpdated: lu, _daysSince: days, _everUpdated: everUpdated };
        });
    }, [vehicles]);

    const filteredStaleRows = useMemo(() => {
        const q = staleSearch.trim().toLowerCase();
        const limit = minDays === "" ? null : Number(minDays);
        const rows = staleRows.filter((v) => {
            if (q && !((v.reg_number || "").toLowerCase().includes(q) || (v.make || "").toLowerCase().includes(q) || (v.model || "").toLowerCase().includes(q))) return false;
            if (limit != null && Number.isFinite(limit) && limit > 0) {
                if (v._daysSince == null || v._daysSince < limit) return false;
            }
            return true;
        });
        rows.sort((a, b) => {
            const ad = a._daysSince ?? -1;
            const bd = b._daysSince ?? -1;
            return sortDir === "desc" ? bd - ad : ad - bd;
        });
        return rows;
    }, [staleRows, staleSearch, minDays, sortDir]);

    const newOdoNum = newOdo === "" ? NaN : Number(newOdo);
    const valid = selected && Number.isInteger(newOdoNum) && newOdoNum > prevOdo;
    const belowOrEqual = selected && Number.isFinite(newOdoNum) && newOdoNum <= prevOdo;
    const diff = valid ? newOdoNum - prevOdo : null;

    const submit = async () => {
        if (!selected) { toast.error("Pick a vehicle"); return; }
        if (!Number.isFinite(newOdoNum) || !Number.isInteger(newOdoNum)) {
            toast.error("Enter a whole number (km)");
            return;
        }
        if (newOdoNum <= prevOdo) {
            toast.error(`New reading must be greater than ${prevOdo.toLocaleString()} km`);
            return;
        }
        setSubmitting(true);
        try {
            // Send the full vehicle doc back with the new odometer so all
            // dependent modules (PM sweep, reports, vehicle detail) pick it up.
            const payload = {
                reg_number: selected.reg_number,
                make: selected.make,
                model: selected.model,
                type: selected.type,
                fuel_type: selected.fuel_type,
                gvw_kg: selected.gvw_kg ?? null,
                capacity: selected.capacity ?? null,
                vehicle_type_code: selected.vehicle_type_code ?? null,
                fixed_mileage_kmpl: selected.fixed_mileage_kmpl ?? null,
                year: selected.year ?? null,
                odometer: newOdoNum,
                location_id: selected.location_id ?? null,
                status: selected.status || "active",
            };
            await api.put(`/vehicles/${selected.id}`, payload);
            toast.success(
                `Updated ${selected.reg_number}: ${prevOdo.toLocaleString()} → ${newOdoNum.toLocaleString()} km (+${(newOdoNum - prevOdo).toLocaleString()})`
            );
            setNewOdo("");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
        } finally {
            setSubmitting(false);
        }
    };

    const exportStaleCsv = () => {
        const today = new Date();
        downloadCsv(`vehicles-by-last-km-update-${today.toISOString().slice(0, 10)}.csv`, filteredStaleRows, [
            ["REG #", "reg_number"],
            ["MAKE", "make"],
            ["MODEL", "model"],
            ["LAST UPDATED", (r) => formatDate(r._lastUpdated)],
            ["DAYS SINCE", (r) => (r._daysSince == null ? "" : r._daysSince)],
            ["ODOMETER (KM)", (r) => (r.odometer || 0)],
            ["STATUS", "status"],
        ]);
    };
    const exportStalePdf = () => printElement(
        staleTableRef.current,
        `EAZYFLEET · Vehicles by last km update · ${filteredStaleRows.length} record(s)`,
    );

    return (
        <div className="p-6 lg:p-8 animate-fade-in" data-testid="odometer-update-page">
            <PageHeader
                eyebrow="FLEET OPERATIONS"
                title="Update vehicle km reading"
                subtitle="New odometer reading propagates to PM schedules, reports and the vehicle master."
            />

            <Surface className="max-w-2xl p-6">
                <div className="space-y-5">
                    <VehicleSearchSelect
                        vehicles={vehicles}
                        value={vehicleId}
                        onChange={setVehicleId}
                        label="Vehicle number"
                        placeholder="Search by registration number…"
                        allowClear
                        testid="odo-vehicle-select"
                    />

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <div className="label-mono mb-1.5">Previous odometer reading</div>
                            <div className={`px-3 py-2 rounded-md border text-sm font-mono ${selected ? "bg-slate-50 border-fleet-border text-fleet-ink" : "bg-slate-50 border-fleet-border text-fleet-inkMuted"}`}
                                data-testid="odo-prev-reading">
                                {selected ? `${prevOdo.toLocaleString()} km` : "—"}
                            </div>
                        </div>
                        <div>
                            <div className="label-mono mb-1.5">Last updated</div>
                            <div className={`px-3 py-2 rounded-md border text-sm font-mono ${selected ? "bg-slate-50 border-fleet-border text-fleet-ink" : "bg-slate-50 border-fleet-border text-fleet-inkMuted"}`}
                                data-testid="odo-last-updated">
                                {selected && lastUpdated ? formatDate(lastUpdated) : "—"}
                            </div>
                        </div>
                    </div>

                    <div>
                        <Input
                            label="New odometer reading (km)"
                            type="number"
                            inputMode="numeric"
                            min={prevOdo + 1}
                            step="1"
                            value={newOdo}
                            onChange={(e) => {
                                // Allow only digits — strip any non-numeric chars
                                const v = e.target.value.replace(/[^\d]/g, "");
                                setNewOdo(v);
                            }}
                            placeholder={selected ? `Must be greater than ${prevOdo.toLocaleString()}` : "Pick a vehicle first"}
                            disabled={!selected}
                            data-testid="odo-new-reading"
                        />
                        {belowOrEqual && (
                            <div className="mt-1.5 text-xs text-fleet-critical flex items-center gap-1">
                                New reading must be greater than the previous reading ({prevOdo.toLocaleString()} km).
                            </div>
                        )}
                        {valid && (
                            <div className="mt-1.5 text-xs text-fleet-healthy flex items-center gap-1">
                                <CheckCircle size={12} /> +{diff.toLocaleString()} km since last update
                            </div>
                        )}
                    </div>

                    <div className="pt-2 flex items-center gap-3">
                        <Button
                            onClick={submit}
                            disabled={!valid || submitting}
                            data-testid="odo-submit"
                        >
                            <Gauge size={14} /> {submitting ? "Updating…" : "Update km reading"}
                        </Button>
                        {selected && (
                            <span className="text-xs text-fleet-inkSoft">
                                Propagates to PM schedules & reports for <span className="font-mono font-medium">{selected.reg_number}</span>.
                            </span>
                        )}
                    </div>
                </div>
            </Surface>

            {/* Stale-km tracker */}
            <Surface className="mt-6 overflow-hidden" data-testid="odo-stale-panel">
                <div className="px-5 py-4 border-b border-fleet-border">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div>
                            <div className="label-mono">FLEET SNAPSHOT</div>
                            <h2 className="font-display text-lg font-semibold mt-0.5">Vehicles by last km update</h2>
                        </div>
                        <span className="text-xs text-fleet-inkSoft">
                            {filteredStaleRows.length} of {staleRows.length} vehicles
                        </span>
                    </div>
                    <div className="mt-2 flex justify-end gap-2 no-print">
                        <Button variant="secondary" size="sm" onClick={exportStaleCsv} data-testid="stale-export-csv">
                            <DownloadSimple size={14} /> CSV
                        </Button>
                        <Button variant="secondary" size="sm" onClick={exportStalePdf} data-testid="stale-export-pdf">
                            <Printer size={14} /> PDF
                        </Button>
                    </div>
                </div>

                <div className="px-5 py-3 border-b border-fleet-border flex items-center gap-3 flex-wrap bg-slate-50/50">
                    <div className="relative flex-1 min-w-[220px] max-w-xs">
                        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fleet-inkMuted" />
                        <input
                            type="text"
                            value={staleSearch}
                            onChange={(e) => setStaleSearch(e.target.value)}
                            placeholder="Search reg # / make…"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink bg-white"
                            data-testid="stale-search"
                        />
                    </div>
                    <div className="inline-flex items-center gap-2">
                        <span className="label-mono">SHOW ONLY ≥</span>
                        <input
                            type="number"
                            min="0"
                            step="1"
                            value={minDays}
                            onChange={(e) => setMinDays(e.target.value.replace(/[^\d]/g, ""))}
                            placeholder="days"
                            className="w-24 px-2 py-1.5 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink bg-white font-mono"
                            data-testid="stale-min-days"
                        />
                        <span className="text-xs text-fleet-inkSoft">day(s) stale</span>
                    </div>
                    <div className="inline-flex items-center gap-1 ml-auto">
                        <span className="label-mono">SORT</span>
                        <button
                            onClick={() => setSortDir((d) => d === "desc" ? "asc" : "desc")}
                            className="flex items-center gap-1 px-2 py-1.5 text-xs border border-fleet-border rounded-md bg-white hover:border-fleet-ink"
                            data-testid="stale-sort"
                        >
                            {sortDir === "desc" ? <><CaretDown size={12} /> Most stale first</> : <><CaretUp size={12} /> Most recent first</>}
                        </button>
                    </div>
                </div>

                <table className="w-full text-sm" ref={staleTableRef}>
                    <thead className="bg-slate-50"><tr className="text-left">
                        {["VEHICLE", "MAKE / MODEL", "LAST UPDATED", "DAYS SINCE", "ODOMETER", ""].map((h) => (
                            <th key={h} className="label-mono px-5 py-3 font-normal">{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {filteredStaleRows.map((v) => {
                            const d = v._daysSince;
                            const tone = d == null ? "text-fleet-inkMuted"
                                : d >= 30 ? "text-fleet-critical"
                                : d >= 14 ? "text-fleet-warning"
                                : "text-fleet-inkSoft";
                            return (
                                <tr key={v.id} className="border-t border-fleet-border data-grid-row" data-testid={`stale-row-${v.id}`}>
                                    <td className="px-5 py-3 font-mono font-medium">{v.reg_number}</td>
                                    <td className="px-5 py-3 text-fleet-inkSoft">{[v.make, v.model].filter(Boolean).join(" · ") || "—"}</td>
                                    <td className="px-5 py-3 font-mono text-xs">
                                        {v._lastUpdated ? formatDate(v._lastUpdated) : "—"}
                                        {!v._everUpdated && <span className="ml-2 text-fleet-inkMuted italic">(never)</span>}
                                    </td>
                                    <td className={`px-5 py-3 font-mono font-semibold ${tone}`}>
                                        {d == null ? "—" : `${d} day${d === 1 ? "" : "s"}`}
                                    </td>
                                    <td className="px-5 py-3 font-mono">{(v.odometer || 0).toLocaleString()} km</td>
                                    <td className="px-5 py-3 text-right">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => { setVehicleId(v.id); setNewOdo(""); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                            data-testid={`stale-update-${v.id}`}
                                        >
                                            Update now
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                        {filteredStaleRows.length === 0 && (
                            <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">
                                {staleRows.length === 0 ? "No vehicles yet." : "No vehicles match the current filters."}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </Surface>
        </div>
    );
}
